import { Alert, Avatar, Badge, Button, ConfigProvider, Empty, Layout, List, Menu, Space, Spin, Tag, Tooltip, Typography, message, Dropdown } from 'antd';
import deDE from 'antd/locale/de_DE';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import frFR from 'antd/locale/fr_FR';
import idID from 'antd/locale/id_ID';
import itIT from 'antd/locale/it_IT';
import jaJP from 'antd/locale/ja_JP';
import msMY from 'antd/locale/ms_MY';
import nlNL from 'antd/locale/nl_NL';
import ptBR from 'antd/locale/pt_BR';
import thTH from 'antd/locale/th_TH';
import zhCN from 'antd/locale/zh_CN';
import {
  BellOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  FormOutlined,
  BuildOutlined,
  GlobalOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
  ShopOutlined,
  LogoutOutlined,
  UserOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AdminAnnouncementsPage from './pages/AdminAnnouncementsPage';
import AdminFormBuilderPage from './pages/AdminFormBuilderPage';
import AdminHelpPage from './pages/AdminHelpPage';
import AdminMasterPage from './pages/AdminMasterPage';
import AdminProductMasterPage from './pages/AdminProductMasterPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import FactoryDetailPage from './pages/FactoryDetailPage';
import FactoryListPage from './pages/FactoryListPage';
import HelpPage from './pages/HelpPage';
import IncidentCreatePage from './pages/IncidentCreatePage';
import IncidentDetailPage from './pages/IncidentDetailPage';
import IncidentListPage from './pages/IncidentListPage';
import MyPage from './pages/MyPage';
import ServiceLocationDetailPage from './pages/ServiceLocationDetailPage';
import ServiceLocationListPage from './pages/ServiceLocationListPage';
import UserManagementPage from './pages/UserManagementPage';
import {
  ApiNetworkError,
  ApiRequestError,
  addIncidentComment,
  addIncidentTaskInternalComment,
  addIncidentAttachment,
  closeIncident,
  createAttachmentDownload,
  createAttachmentThumbnailDownload,
  createAttachmentPresign,
  createIncident,
  createIncidentDraft,
  createIncidentRequest,
  deleteIncidentAttachment,
  deleteIncidentComment,
  deleteIncidentTaskInternalComment,
  deleteTaskActivity,
  getCurrentUser,
  getAnnouncementUnreadCount,
  getProcessTimeline,
  listAppLanguages,
  listAnnouncements,
  listIncidentRequestActivityEdits,
  listIncidentRequestEdits,
  listIncidentRequests,
  listIncidentTasks,
  listIncidentAttachments,
  listIncidentComments,
  listIncidentTaskInternalComments,
  listIncidentFieldChanges,
  listIncidentStatusHistory,
  listIncidents,
  listTaskActivities,
  listTaskActivityEdits,
  markAllAnnouncementsRead,
  markAnnouncementRead,
  setIncidentUseful,
  setIncidentWatch,
  updateIncidentRequest,
  updateIncidentRequestActivity,
  updateIncidentTask,
  updateTaskActivity,
  updateIncident,
  updateIncidentComment,
  updateIncidentTaskInternalComment,
  updateMyProfile,
} from './services/api';
import type {
  Announcement,
  AppLanguage,
  CurrentUser,
  AuditEdit,
  Incident,
  IncidentFieldChange,
  IncidentStatusHistory,
  IncidentComment,
  IncidentTaskInternalComment,
  IncidentCreate,
  ProcessTimeline,
  IncidentStatus,
  IncidentUpdate,
  AttachmentCategory,
  IncidentAttachment,
  IncidentRequest,
  IncidentRequestCreate,
  IncidentRequestUpdate,
  IncidentRequestActivity,
  IncidentRequestActivityUpdate,
  TaskActivity,
  TaskActivityUpdate,
  IncidentTask,
  IncidentTaskUpdate,
} from './types/incident';
import {
  AuthSessionExpiredError,
  consumePostLoginRedirectPath,
  getAuthUser,
  isAuthConfigured,
  isManualLogout,
  login,
  logout,
  subscribeAuthRefresh,
  type AuthUser,
} from './services/auth';
import { canCreateIncident, filterIncidentsByAssignment } from './assignments';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
type UiLanguage = string;
type AppInitPhase = 'bootstrapping' | 'unauthenticated' | 'initializationError' | 'ready';
type InitializationError = {
  title: string;
  message: string;
};

const isUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const taskInternalCommentKey = (incidentId: string, taskId: string) => `${incidentId}:${taskId}`;

const today = new Date().toISOString().slice(0, 10);
const maxAttachmentSizeBytes = 50 * 1024 * 1024;

const seedIncidents: Incident[] = [
  {
    id: 'seed-1',
    incident_number: 'GIB-000000',
    primary_language: 'en',
    title: 'Prototype sample: abnormal startup noise',
    status: 'OPEN',
    severity: 'high',
    commissioning_date: '2026-04-01',
    trouble_date: today,
    claim_receive_date: today,
    product_classification: 'Commercial HVAC',
    defect_location: 'Motor unit',
    installation_environment: 'Indoor plant room / standard installation',
    info_classification: 'Field claim',
    site_resolution_status: 'Temporary recovery',
    initial_comment: 'Regional service team requests factory quality support for abnormal startup noise.',
    claim_class: 'A',
    related_gib_no: null,
    model_name: 'PX series',
    model_number: 'PX-1000',
    serial_no: 'SN-PX1000-0001',
    lot_number: 'L2026-04',
    is_new_product: false,
    unit_count: 1,
    production_factory: 'Factory A',
    production_factory_setting_date: null,
    season: 'Winter',
    piping_length: null,
    error_code: null,
    room_size: null,
    dealer: null,
    delivery_destination: 'Sample customer site',
    installer_name: null,
    delivery_route: null,
    service_control_no: null,
    customer_impact: 'Customer line restart delayed by 30 minutes.',
    phenomenon: 'Startup noise was reported from an EU field unit during cold boot.',
    temporary_action: 'Service team collected logs and isolated the returned unit.',
    assignee_user_id: null,
    defect_tag_ids: [],
    defect_tags: [],
    watcher_count: 0,
    useful_count: 0,
    is_watched_by_me: false,
    is_marked_useful_by_me: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const createSeedTasks = (incidentId: string): IncidentTask[] => [
  {
    id: 'task-seed-service',
    incident_id: incidentId,
    task_type: 'SERVICE_RESPONSE',
    responsible_role: 'SERVICE',
    status: 'OPEN',
    response_summary: null,
    action_taken: null,
    root_cause: null,
    hold_reason: null,
    requested_to_role: null,
    requested_action: null,
    final_response_due_date: null,
    resolved_at: null,
    closed_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'task-seed-quality',
    incident_id: incidentId,
    task_type: 'QUALITY_RESPONSE',
    responsible_role: 'QUALITY',
    status: 'OPEN',
    response_summary: null,
    action_taken: null,
    root_cause: null,
    hold_reason: null,
    requested_to_role: null,
    requested_action: null,
    final_response_due_date: null,
    resolved_at: null,
    closed_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const createLocalTasks = (incidentId: string): IncidentTask[] => [
  {
    id: crypto.randomUUID(),
    incident_id: incidentId,
    task_type: 'SERVICE_RESPONSE',
    responsible_role: 'SERVICE',
    status: 'OPEN',
    response_summary: null,
    action_taken: null,
    root_cause: null,
    hold_reason: null,
    requested_to_role: null,
    requested_action: null,
    final_response_due_date: null,
    resolved_at: null,
    closed_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: crypto.randomUUID(),
    incident_id: incidentId,
    task_type: 'QUALITY_RESPONSE',
    responsible_role: 'QUALITY',
    status: 'OPEN',
    response_summary: null,
    action_taken: null,
    root_cause: null,
    hold_reason: null,
    requested_to_role: null,
    requested_action: null,
    final_response_due_date: null,
    resolved_at: null,
    closed_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const createLocalHistory = (
  incidentId: string,
  toStatus: IncidentStatus,
  reason: string | null = null,
  fromStatus: IncidentStatus | null = null
): IncidentStatusHistory => ({
  id: crypto.randomUUID(),
  incident_id: incidentId,
  changed_by_user_id: null,
  changed_by_display_name: 'Prototype User',
  from_status: fromStatus,
  to_status: toStatus,
  reason,
  created_at: new Date().toISOString(),
});

const inferAttachmentCategory = (file: File): AttachmentCategory => {
  const contentType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  if (contentType.startsWith('image/')) return 'PHOTO';
  if (contentType.startsWith('video/')) return 'VIDEO';
  if (fileName.endsWith('.csv') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) return 'CHECKER_DATA';
  if (fileName.endsWith('.log') || fileName.endsWith('.txt') || fileName.endsWith('.json')) return 'LOG';
  return 'OTHER';
};

const deriveIncidentStatusFromTasks = (
  currentStatus: IncidentStatus,
  tasks: IncidentTask[]
): IncidentStatus => {
  if (currentStatus === 'DRAFT' || currentStatus === 'CLOSED') return currentStatus;
  const serviceTask = tasks.find((task) => task.task_type === 'SERVICE_RESPONSE');
  const qualityTask = tasks.find((task) => task.task_type === 'QUALITY_RESPONSE');
  const confirmationTask = tasks.find((task) => task.task_type === 'ISSUING_PARTY_CONFIRMATION');
  const serviceDone = serviceTask && ['RESOLVED', 'CLOSED'].includes(serviceTask.status);
  const qualityDone = qualityTask && ['RESOLVED', 'CLOSED'].includes(qualityTask.status);
  if (serviceDone && qualityDone && confirmationTask?.status === 'CLOSED') return 'CLOSED';
  if (serviceDone && qualityDone) return 'RESOLVED';
  if (tasks.some((task) => ['IN_PROGRESS', 'ON_HOLD', 'RESOLVED'].includes(task.status))) return 'IN_PROGRESS';
  return 'OPEN';
};

const formatAnnouncementDate = (value: string, language: string): string =>
  new Intl.DateTimeFormat(language.startsWith('ja') ? 'ja-JP' : 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

type NotificationBellProps = {
  announcements: Announcement[];
  unreadCount: number;
  loading: boolean;
  onOpenAnnouncements: () => void;
  onMarkRead: (announcementId: string) => void;
  onMarkAllRead: () => void;
};

const NotificationBell = ({
  announcements,
  unreadCount,
  loading,
  onOpenAnnouncements,
  onMarkRead,
  onMarkAllRead,
}: NotificationBellProps) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const latestAnnouncements = announcements.slice(0, 5);
  const popup = (
    <div className="notificationPanel">
      <div className="notificationPanelHeader">
        <Text strong>{t('announcements.title')}</Text>
        <Button type="link" size="small" disabled={unreadCount === 0} onClick={onMarkAllRead}>
          {t('announcements.markAllRead')}
        </Button>
      </div>
      {latestAnnouncements.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('announcements.empty')} />
      ) : (
        <List
          className="notificationList"
          loading={loading}
          dataSource={latestAnnouncements}
          renderItem={(announcement) => (
            <List.Item
              className={announcement.is_read ? 'notificationItem isRead' : 'notificationItem'}
              onClick={() => {
                if (!announcement.is_read) onMarkRead(announcement.id);
              }}
            >
              <div className="notificationItemBody">
                <Space size={6} wrap>
                  {!announcement.is_read && <span className="unreadDot" />}
                  <Text strong={!announcement.is_read}>{announcement.title}</Text>
                  {announcement.severity !== 'info' && (
                    <Tag color={announcement.severity === 'critical' ? 'red' : 'gold'}>
                      {t(`announcements.severity.${announcement.severity}`)}
                    </Tag>
                  )}
                </Space>
                <Text type="secondary" className="notificationDate">
                  {formatAnnouncementDate(announcement.published_at, i18n.language)}
                </Text>
                <Text className="notificationBody">{announcement.body}</Text>
              </div>
            </List.Item>
          )}
        />
      )}
      <Button
        block
        type="link"
        onClick={() => {
          setOpen(false);
          onOpenAnnouncements();
        }}
      >
        {t('announcements.viewAll')}
      </Button>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => popup}
      onOpenChange={setOpen}
      open={open}
      trigger={['click']}
      placement="bottomRight"
    >
      <Badge className="headerNotificationBadge" count={unreadCount} size="small" overflowCount={99}>
        <Button
          aria-label={t('announcements.notifications')}
          className="headerIconButton"
          icon={<BellOutlined />}
          loading={loading && announcements.length === 0}
        />
      </Badge>
    </Dropdown>
  );
};

type UserMenuProps = {
  currentUser: CurrentUser | null;
  isAuthConfigured: boolean;
  languages: AppLanguage[];
  onNavigateMyPage: () => void;
  onLanguageChange: (language: UiLanguage) => void;
};

const UserMenu = ({ currentUser, isAuthConfigured, languages, onNavigateMyPage, onLanguageChange }: UserMenuProps) => {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;
  const menuLanguages = languages.length > 0
    ? languages
    : [
        { code: 'ja', name: 'Japanese', native_name: '日本語', is_active: true, sort_order: 10 },
        { code: 'en', name: 'English', native_name: 'English', is_active: true, sort_order: 20 },
        { code: 'zh-CN', name: 'Chinese (Simplified)', native_name: '简体中文', is_active: true, sort_order: 30 },
        { code: 'th', name: 'Thai', native_name: 'ไทย', is_active: true, sort_order: 40 },
        { code: 'ms', name: 'Malay', native_name: 'Bahasa Melayu', is_active: true, sort_order: 50 },
        { code: 'id', name: 'Indonesian', native_name: 'Bahasa Indonesia', is_active: true, sort_order: 60 },
        { code: 'it', name: 'Italian', native_name: 'Italiano', is_active: true, sort_order: 70 },
        { code: 'de', name: 'German', native_name: 'Deutsch', is_active: true, sort_order: 80 },
        { code: 'fr', name: 'French', native_name: 'Français', is_active: true, sort_order: 90 },
        { code: 'es', name: 'Spanish', native_name: 'Español', is_active: true, sort_order: 100 },
        { code: 'pt-BR', name: 'Portuguese (Brazil)', native_name: 'Português (Brasil)', is_active: true, sort_order: 110 },
        { code: 'nl', name: 'Dutch', native_name: 'Nederlands', is_active: true, sort_order: 120 },
      ];

  return (
    <Dropdown
      placement="bottomRight"
      trigger={['click']}
      menu={{
        items: [
          { key: 'myPage', icon: <UserOutlined />, label: t('app.nav.myPage'), onClick: onNavigateMyPage },
          { type: 'divider' },
          { key: 'languageLabel', icon: <GlobalOutlined />, label: t('app.language'), disabled: true },
          ...menuLanguages.map((language) => ({
            key: `language-${language.code}`,
            label: language.native_name,
            onClick: () => onLanguageChange(language.code),
            className: currentLanguage === language.code ? 'isSelectedMenuItem' : undefined,
          })),
          ...(isAuthConfigured
            ? [
                { type: 'divider' as const },
                { key: 'logout', icon: <LogoutOutlined />, label: t('auth.signOut'), onClick: () => logout() },
              ]
            : []),
        ],
      }}
    >
      <button className="userSummaryButton" type="button">
        <Avatar size={28} src={currentUser?.profile_image_url || undefined} icon={<UserOutlined />} />
        <Text className="userSummaryName">{currentUser?.display_name || currentUser?.email || ''}</Text>
      </button>
    </Dropdown>
  );
};

type AnnouncementsPageProps = {
  announcements: Announcement[];
  loading: boolean;
  onMarkRead: (announcementId: string) => void;
  onMarkAllRead: () => void;
};

const AnnouncementsPage = ({ announcements, loading, onMarkRead, onMarkAllRead }: AnnouncementsPageProps) => {
  const { i18n, t } = useTranslation();
  return (
    <div className="page announcementsPage">
      <div className="pageHeaderRow">
        <div>
          <h1>{t('announcements.title')}</h1>
          <p>{t('announcements.description')}</p>
        </div>
        <Button disabled={announcements.every((announcement) => announcement.is_read)} onClick={onMarkAllRead}>
          {t('announcements.markAllRead')}
        </Button>
      </div>
      <List
        className="announcementsList"
        loading={loading}
        dataSource={announcements}
        locale={{ emptyText: t('announcements.empty') }}
        renderItem={(announcement) => (
          <List.Item
            className={announcement.is_read ? 'announcementListItem isRead' : 'announcementListItem'}
            actions={[
              announcement.is_read ? (
                <Text type="secondary">{t('announcements.read')}</Text>
              ) : (
                <Button type="link" onClick={() => onMarkRead(announcement.id)}>
                  {t('announcements.markRead')}
                </Button>
              ),
            ]}
          >
            <div className="announcementListContent">
              <Space size={8} wrap>
                {!announcement.is_read && <span className="unreadDot" />}
                <Text strong={!announcement.is_read}>{announcement.title}</Text>
                {announcement.severity !== 'info' && (
                  <Tag color={announcement.severity === 'critical' ? 'red' : 'gold'}>
                    {t(`announcements.severity.${announcement.severity}`)}
                  </Tag>
                )}
              </Space>
              <Text type="secondary" className="announcementDate">
                {formatAnnouncementDate(announcement.published_at, i18n.language)}
              </Text>
              <Text className="announcementBody">{announcement.body}</Text>
            </div>
          </List.Item>
        )}
      />
    </div>
  );
};

const AppShell = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [incidents, setIncidents] = useState<Incident[]>(seedIncidents);
  const [localComments, setLocalComments] = useState<Record<string, IncidentComment[]>>({
    'seed-1': [
      {
        id: 'comment-seed-1',
        incident_id: 'seed-1',
        author_user_id: null,
        body: 'Regional service team collected the returned unit and shared initial field notes.',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ],
  });
  const localCommentsRef = useRef(localComments);
  const [localTaskInternalComments, setLocalTaskInternalComments] = useState<Record<string, IncidentTaskInternalComment[]>>({});
  const localTaskInternalCommentsRef = useRef(localTaskInternalComments);
  const [localAttachments, setLocalAttachments] = useState<Record<string, IncidentAttachment[]>>({});
  const localAttachmentsRef = useRef(localAttachments);
  const [localTasks, setLocalTasks] = useState<Record<string, IncidentTask[]>>({
    'seed-1': createSeedTasks('seed-1'),
  });
  const localTasksRef = useRef(localTasks);
  const [, setLocalStatusHistory] = useState<Record<string, IncidentStatusHistory[]>>({
    'seed-1': [createLocalHistory('seed-1', 'OPEN', 'Incident created')],
  });
  const [localTaskActivities, setLocalTaskActivities] = useState<Record<string, TaskActivity[]>>({});
  const localTaskActivitiesRef = useRef(localTaskActivities);
  const [localFieldChanges, setLocalFieldChanges] = useState<Record<string, IncidentFieldChange[]>>({});
  const localFieldChangesRef = useRef(localFieldChanges);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(isAuthConfigured);
  const [currentUserLoading, setCurrentUserLoading] = useState(true);
  const [initializationError, setInitializationError] = useState<InitializationError | null>(null);
  const [sessionRefreshing, setSessionRefreshing] = useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState<string>('ALL');
  const [loading, setLoading] = useState(true);
  const [apiWarning, setApiWarning] = useState<string | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0);
  const [appLanguages, setAppLanguages] = useState<AppLanguage[]>([]);
  const isPublicEmailVerificationLink = useMemo(() => {
    if (!location.pathname.startsWith('/email-verification')) return false;
    return new URLSearchParams(location.search).has('token');
  }, [location.pathname, location.search]);

  useEffect(() => subscribeAuthRefresh(setSessionRefreshing), []);

  useEffect(() => {
    let cancelled = false;
    getAuthUser()
      .then((user) => {
        if (!cancelled) {
          setAuthUser(user);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAuthLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthConfigured || authLoading || authUser || isManualLogout() || isPublicEmailVerificationLink) return;
    login();
  }, [authLoading, authUser, isPublicEmailVerificationLink]);

  useEffect(() => {
    if (!isAuthConfigured || !authUser) return;
    const returnTo = consumePostLoginRedirectPath();
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (returnTo && returnTo !== currentPath) {
      navigate(returnTo, { replace: true });
    }
  }, [authUser, location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (isPublicEmailVerificationLink && (!isAuthConfigured || !authUser)) {
      setCurrentUserLoading(false);
      return;
    }
    if (isAuthConfigured && authLoading) return;
    if (isAuthConfigured && !authUser) {
      setCurrentUserLoading(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setCurrentUserLoading(true);
    setInitializationError(null);
    getCurrentUser()
      .then((user) => {
        if (cancelled) return;
        setCurrentUser(user);
        setInitializationError(null);
      })
      .catch((error) => {
        if (!cancelled && !isAuthConfigured) {
          setCurrentUser({
            id: 'local-user',
            cognito_sub: 'local-user',
            email: 'prototype.user@example.com',
            display_name: 'Prototype User',
            preferred_ui_language: 'ja',
            email_is_validated: true,
            email_validated_at: null,
            email_validation_requested_at: null,
            roles: [],
            assignments: [],
          });
          setInitializationError(null);
        } else if (
          !cancelled &&
          (error instanceof AuthSessionExpiredError || (error instanceof ApiRequestError && error.status === 401))
        ) {
          setCurrentUser(null);
          login();
        } else if (!cancelled) {
          setCurrentUser(null);
          const isServerError =
            error instanceof ApiNetworkError ||
            (error instanceof ApiRequestError && error.status >= 500);
          setInitializationError({
            title: isServerError ? t('auth.serverErrorTitle') : t('auth.initializationErrorTitle'),
            message: isServerError ? t('auth.serverError') : t('auth.initializationError'),
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCurrentUserLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, authUser, isPublicEmailVerificationLink, t]);

  useEffect(() => {
    if (!currentUser?.preferred_ui_language) return;
    const currentLanguage = i18n.language;
    if (currentLanguage !== currentUser.preferred_ui_language) {
      i18n.changeLanguage(currentUser.preferred_ui_language);
    }
  }, [currentUser?.preferred_ui_language, i18n]);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    listAppLanguages()
      .then((languages) => {
        if (!cancelled) setAppLanguages(languages);
      })
      .catch(() => {
        if (!cancelled) setAppLanguages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const loadAnnouncements = useCallback(async () => {
    if (!currentUser) return;
    setAnnouncementsLoading(true);
    try {
      const [items, unread] = await Promise.all([
        listAnnouncements(),
        getAnnouncementUnreadCount(),
      ]);
      setAnnouncements(items);
      setAnnouncementUnreadCount(unread.unread_count);
    } catch {
      setAnnouncements([]);
      setAnnouncementUnreadCount(0);
    } finally {
      setAnnouncementsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  const handleLanguageChange = useCallback(async (language: UiLanguage) => {
    await i18n.changeLanguage(language);
    setCurrentUser((user) => (user ? { ...user, preferred_ui_language: language } : user));
    try {
      const updated = await updateMyProfile({ preferred_ui_language: language });
      setCurrentUser(updated);
    } catch {
      messageApi.error(t('app.messages.languageSaveFailed'));
    }
  }, [i18n, messageApi, t]);

  const handleMarkAnnouncementRead = useCallback(async (announcementId: string) => {
    setAnnouncements((current) =>
      current.map((announcement) =>
        announcement.id === announcementId
          ? { ...announcement, is_read: true, read_at: announcement.read_at || new Date().toISOString() }
          : announcement
      )
    );
    setAnnouncementUnreadCount((current) => Math.max(0, current - 1));
    try {
      await markAnnouncementRead(announcementId);
      await loadAnnouncements();
    } catch {
      messageApi.error(t('announcements.messages.markReadFailed'));
      await loadAnnouncements();
    }
  }, [loadAnnouncements, messageApi, t]);

  const handleMarkAllAnnouncementsRead = useCallback(async () => {
    setAnnouncements((current) =>
      current.map((announcement) => ({
        ...announcement,
        is_read: true,
        read_at: announcement.read_at || new Date().toISOString(),
      }))
    );
    setAnnouncementUnreadCount(0);
    try {
      await markAllAnnouncementsRead();
      await loadAnnouncements();
    } catch {
      messageApi.error(t('announcements.messages.markReadFailed'));
      await loadAnnouncements();
    }
  }, [loadAnnouncements, messageApi, t]);

  useEffect(() => {
    if (isPublicEmailVerificationLink) {
      setLoading(false);
      return;
    }
    if (isAuthConfigured && (authLoading || !authUser)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    listIncidents()
      .then((items) => {
        if (!cancelled) {
          setIncidents(items.length > 0 ? items : seedIncidents);
          setApiWarning(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          if (isAuthConfigured) {
            setIncidents([]);
          }
          setApiWarning(t('app.messages.backendUnavailable'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, authUser, isPublicEmailVerificationLink, t]);

  const appInitPhase: AppInitPhase = useMemo(() => {
    if (isPublicEmailVerificationLink) return 'ready';
    if (authLoading) return 'bootstrapping';
    if (isAuthConfigured && !authUser) return 'unauthenticated';
    if (initializationError) return 'initializationError';
    if (currentUserLoading || loading) return 'bootstrapping';
    if (isAuthConfigured && !currentUser) return 'initializationError';
    return 'ready';
  }, [authLoading, authUser, currentUser, currentUserLoading, initializationError, isPublicEmailVerificationLink, loading]);

  useEffect(() => {
    localCommentsRef.current = localComments;
  }, [localComments]);

  useEffect(() => {
    localTaskInternalCommentsRef.current = localTaskInternalComments;
  }, [localTaskInternalComments]);

  useEffect(() => {
    localAttachmentsRef.current = localAttachments;
  }, [localAttachments]);

  useEffect(() => {
    localTasksRef.current = localTasks;
  }, [localTasks]);

  useEffect(() => {
    localTaskActivitiesRef.current = localTaskActivities;
  }, [localTaskActivities]);

  useEffect(() => {
    localFieldChangesRef.current = localFieldChanges;
  }, [localFieldChanges]);

  useEffect(() => {
    const remoteIncidents = incidents.filter((incident) => isUuid(incident.id));
    if (remoteIncidents.length === 0) return;
    let cancelled = false;

    Promise.all(
      remoteIncidents.map(async (incident) => {
        const [tasks, attachments] = await Promise.all([
          listIncidentTasks(incident.id).catch(() => localTasksRef.current[incident.id] || []),
          listIncidentAttachments(incident.id).catch(() => localAttachmentsRef.current[incident.id] || []),
        ]);
        return { incidentId: incident.id, tasks, attachments };
      })
    ).then((items) => {
      if (cancelled) return;
      setLocalTasks((current) => {
        const next = { ...current };
        items.forEach((item) => {
          next[item.incidentId] = item.tasks;
        });
        return next;
      });
      setLocalAttachments((current) => {
        const next = { ...current };
        items.forEach((item) => {
          next[item.incidentId] = item.attachments;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [incidents]);

  const appendLocalStatusHistory = useCallback((
    incidentId: string,
    fromStatus: IncidentStatus | null,
    toStatus: IncidentStatus,
    reason: string | null
  ) => {
    setLocalStatusHistory((current) => ({
      ...current,
      [incidentId]: [
        ...(current[incidentId] || []),
        createLocalHistory(incidentId, toStatus, reason, fromStatus),
      ],
    }));
  }, []);

  const selectedKey = useMemo(() => {
    if (location.pathname.startsWith('/email-verification')) return '';
    if (location.pathname.startsWith('/announcements')) return '';
    if (location.pathname.startsWith('/help')) return '/help';
    if (location.pathname.startsWith('/me')) return '/me';
    if (location.pathname.startsWith('/admin/announcements')) return '/admin/announcements';
    if (location.pathname.startsWith('/admin/forms')) return '/admin/forms';
    if (location.pathname.startsWith('/admin/help')) return '/admin/help';
    if (location.pathname.startsWith('/admin/master/factories')) return '/admin/master/factories';
    if (location.pathname.startsWith('/admin/master/products')) return '/admin/master/products';
    if (location.pathname.startsWith('/admin/master/service-locations')) return '/admin/master/service-locations';
    if (location.pathname.startsWith('/admin/users')) return '/admin/users';
    if (location.pathname.startsWith('/factories')) return '/factories';
    if (location.pathname.startsWith('/service-locations')) return '/service-locations';
    if (location.pathname.startsWith('/incidents/new')) return '/incidents/new';
    if (location.pathname.startsWith('/incidents')) return '/incidents';
    return '/me';
  }, [location.pathname]);

  const visibleIncidents = useMemo(
    () => filterIncidentsByAssignment(incidents, currentUser?.assignments || [], assignmentFilter),
    [assignmentFilter, currentUser?.assignments, incidents]
  );
  const canCreate = canCreateIncident(currentUser?.assignments || []);
  const canAccessUserManagement = currentUser?.assignments.some(
    (assignment) => assignment.role === 'SYSTEM_ADMIN' && assignment.is_active
  ) || false;
  const pageTitle = useMemo(() => {
    if (location.pathname.startsWith('/email-verification')) return t('emailVerification.pageTitle');
    if (location.pathname.startsWith('/announcements')) return t('announcements.title');
    if (location.pathname.startsWith('/help')) return t('help.title');
    if (location.pathname.startsWith('/me')) return t('myPage.title');
    if (location.pathname.startsWith('/admin/announcements')) return t('adminAnnouncements.title');
    if (location.pathname.startsWith('/admin/forms')) return t('adminFormBuilder.title');
    if (location.pathname.startsWith('/admin/help')) return t('adminHelp.title');
    if (location.pathname.startsWith('/admin/master/factories')) return t('adminMaster.factories.title');
    if (location.pathname.startsWith('/admin/master/products')) return t('adminProductMaster.title');
    if (location.pathname.startsWith('/admin/master/service-locations')) return t('adminMaster.serviceLocations.title');
    if (location.pathname.startsWith('/admin/users')) return t('adminUsers.title');
    if (location.pathname === '/factories') return t('factory.list.title');
    if (location.pathname.startsWith('/factories/')) return t('app.pageTitles.factoryDetail');
    if (location.pathname === '/service-locations') return t('serviceLocation.list.title');
    if (location.pathname.startsWith('/service-locations/')) return t('app.pageTitles.serviceLocationDetail');
    if (location.pathname.startsWith('/incidents/new')) return t('incident.create.title');
    if (location.pathname.startsWith('/incidents/')) {
      return t('app.pageTitles.incidentDetail');
    }
    if (location.pathname.startsWith('/incidents')) return t('incident.list.title');
    return t('myPage.title');
  }, [location.pathname, t]);
  const pageHelpHref = useMemo(() => {
    if (location.pathname.startsWith('/admin/announcements')) return '/help/announcement-management';
    if (location.pathname.startsWith('/admin/forms')) return '/help/form-builder';
    if (location.pathname.startsWith('/admin/help')) return '/help/help-editor';
    if (location.pathname.startsWith('/incidents/new')) return '/help/incident-create';
    if (location.pathname.startsWith('/incidents/')) return '/help/incident-detail';
    return null;
  }, [location.pathname]);

  const handleCreate = async (payload: IncidentCreate): Promise<string> => {
    try {
      const incident = await createIncident(payload);
      setIncidents((current) => [incident, ...current.filter((item) => !item.id.startsWith('seed-'))]);
      messageApi.success(t('app.messages.incidentCreated'));
      return incident.id;
    } catch {
      const incident: Incident = {
        ...payload,
        product_classification: payload.product_classification || 'Unspecified',
        id: crypto.randomUUID(),
        incident_number: `GIB-LOCAL-${incidents.length + 1}`,
        primary_language: i18n.language.startsWith('ja') ? 'ja' : i18n.language,
        status: 'OPEN',
        defect_tag_ids: payload.defect_tag_ids || [],
        defect_tags: [],
        watcher_count: 0,
        useful_count: 0,
        is_watched_by_me: false,
        is_marked_useful_by_me: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setIncidents((current) => [incident, ...current]);
      setLocalTasks((current) => ({ ...current, [incident.id]: createLocalTasks(incident.id) }));
      setLocalStatusHistory((current) => ({
        ...current,
        [incident.id]: [createLocalHistory(incident.id, 'OPEN', 'Incident created')],
      }));
      messageApi.warning(t('app.messages.localIncidentCreated'));
      return incident.id;
    }
  };

  const handleDraft = async (text: string): Promise<Partial<IncidentCreate>> => {
    try {
      const draft = await createIncidentDraft(text);
      return draft;
    } catch {
      messageApi.warning(t('app.messages.draftFallback'));
      return {
        title: text.split('\n')[0].slice(0, 120),
        phenomenon: text,
        severity: text.includes('停止') ? 'critical' : 'medium',
        temporary_action: '現品確認、ロット隔離、再現条件の記録を推奨',
      };
    }
  };

  const handleUpdateIncident = useCallback(async (
    incidentId: string,
    payload: IncidentUpdate
  ): Promise<Incident> => {
    const previous = incidents.find((incident) => incident.id === incidentId);
    if (!previous) {
      throw new Error('Incident not found');
    }

    const optimistic: Incident = {
      ...previous,
      ...payload,
      updated_at: new Date().toISOString(),
    };
    setIncidents((current) => current.map((incident) => (incident.id === incidentId ? optimistic : incident)));

    if (!isUuid(incidentId)) {
      messageApi.warning(t('app.messages.localIncidentUpdated'));
      return optimistic;
    }

    try {
      const updated = await updateIncident(incidentId, payload);
      setIncidents((current) => current.map((incident) => (incident.id === incidentId ? updated : incident)));
      messageApi.success(t('app.messages.incidentUpdated'));
      return updated;
    } catch {
      messageApi.warning(t('app.messages.remoteIncidentUpdatedLocally'));
      return optimistic;
    }
  }, [incidents, messageApi, t]);

  const handleSetIncidentWatch = useCallback(async (incidentId: string, enabled: boolean): Promise<Incident> => {
    const previous = incidents.find((incident) => incident.id === incidentId);
    if (!previous) throw new Error('Incident not found');
    const updated = await setIncidentWatch(incidentId, enabled);
    setIncidents((current) => current.map((incident) => (incident.id === incidentId ? updated : incident)));
    messageApi.success(enabled ? t('incident.detail.reactions.watchAdded') : t('incident.detail.reactions.watchRemoved'));
    return updated;
  }, [incidents, messageApi, t]);

  const handleSetIncidentUseful = useCallback(async (incidentId: string, enabled: boolean): Promise<Incident> => {
    const previous = incidents.find((incident) => incident.id === incidentId);
    if (!previous) throw new Error('Incident not found');
    const updated = await setIncidentUseful(incidentId, enabled);
    setIncidents((current) => current.map((incident) => (incident.id === incidentId ? updated : incident)));
    messageApi.success(enabled ? t('incident.detail.reactions.usefulAdded') : t('incident.detail.reactions.usefulRemoved'));
    return updated;
  }, [incidents, messageApi, t]);

  const handleLoadComments = useCallback(async (incidentId: string): Promise<IncidentComment[]> => {
    if (!isUuid(incidentId)) {
      return localCommentsRef.current[incidentId] || [];
    }

    try {
      const comments = await listIncidentComments(incidentId);
      setLocalComments((current) => ({ ...current, [incidentId]: comments }));
      return comments;
    } catch {
      return localCommentsRef.current[incidentId] || [];
    }
  }, []);

  const handleLoadTaskInternalComments = useCallback(async (
    incidentId: string,
    taskId: string
  ): Promise<IncidentTaskInternalComment[]> => {
    const cacheKey = taskInternalCommentKey(incidentId, taskId);
    if (!isUuid(incidentId) || !isUuid(taskId)) {
      return localTaskInternalCommentsRef.current[cacheKey] || [];
    }

    try {
      const comments = await listIncidentTaskInternalComments(incidentId, taskId);
      setLocalTaskInternalComments((current) => ({ ...current, [cacheKey]: comments }));
      return comments;
    } catch {
      return localTaskInternalCommentsRef.current[cacheKey] || [];
    }
  }, []);

  const handleLoadTasks = useCallback(async (incidentId: string): Promise<IncidentTask[]> => {
    if (!isUuid(incidentId)) {
      return localTasksRef.current[incidentId] || [];
    }

    try {
      const tasks = await listIncidentTasks(incidentId);
      setLocalTasks((current) => ({ ...current, [incidentId]: tasks }));
      return tasks;
    } catch {
      return localTasksRef.current[incidentId] || [];
    }
  }, []);

  const handleLoadRequests = useCallback(async (incidentId: string): Promise<IncidentRequest[]> => {
    if (!isUuid(incidentId)) {
      return [];
    }
    return listIncidentRequests(incidentId);
  }, []);

  const handleCreateRequest = useCallback(async (
    incidentId: string,
    payload: IncidentRequestCreate
  ): Promise<IncidentRequest> => {
    if (!isUuid(incidentId)) {
      throw new Error('Requests are available only for saved incidents');
    }
    return createIncidentRequest(incidentId, payload);
  }, []);

  const handleUpdateRequest = useCallback(async (
    incidentId: string,
    requestId: string,
    payload: IncidentRequestUpdate
  ): Promise<IncidentRequest> => {
    if (!isUuid(incidentId)) {
      throw new Error('Requests are available only for saved incidents');
    }
    return updateIncidentRequest(incidentId, requestId, payload);
  }, []);

  const handleLoadRequestEdits = useCallback(async (incidentId: string, requestId: string): Promise<AuditEdit[]> => {
    if (!isUuid(incidentId)) return [];
    return listIncidentRequestEdits(incidentId, requestId);
  }, []);

  const handleUpdateRequestActivity = useCallback(async (
    incidentId: string,
    requestId: string,
    activityId: string,
    payload: IncidentRequestActivityUpdate
  ): Promise<IncidentRequestActivity> => {
    if (!isUuid(incidentId)) {
      throw new Error('Requests are available only for saved incidents');
    }
    return updateIncidentRequestActivity(incidentId, requestId, activityId, payload);
  }, []);

  const handleLoadRequestActivityEdits = useCallback(async (
    incidentId: string,
    requestId: string,
    activityId: string
  ): Promise<AuditEdit[]> => {
    if (!isUuid(incidentId)) return [];
    return listIncidentRequestActivityEdits(incidentId, requestId, activityId);
  }, []);

  const handleLoadAttachments = useCallback(async (incidentId: string): Promise<IncidentAttachment[]> => {
    if (!isUuid(incidentId)) {
      return localAttachmentsRef.current[incidentId] || [];
    }

    try {
      const attachments = await listIncidentAttachments(incidentId);
      setLocalAttachments((current) => ({ ...current, [incidentId]: attachments }));
      return attachments;
    } catch {
      return localAttachmentsRef.current[incidentId] || [];
    }
  }, []);

  const handleUpdateTask = useCallback(async (
    incidentId: string,
    taskId: string,
    payload: IncidentTaskUpdate
  ): Promise<IncidentTask> => {
    const currentTasks = localTasksRef.current[incidentId] || [];
    const previousTask = currentTasks.find((task) => task.id === taskId);
    if (!previousTask) {
      throw new Error('Task not found');
    }

    const now = new Date().toISOString();
    const optimistic: IncidentTask = {
      ...previousTask,
      ...payload,
      hold_reason: payload.status === 'ON_HOLD' ? payload.hold_reason || null : payload.hold_reason ?? null,
      requested_to_role: payload.status === 'ON_HOLD' ? payload.requested_to_role || null : payload.requested_to_role ?? null,
      requested_action: payload.status === 'ON_HOLD' ? payload.requested_action || null : payload.requested_action ?? null,
      resolved_at: payload.status === 'RESOLVED' ? previousTask.resolved_at || now : previousTask.resolved_at,
      closed_at: payload.status === 'CLOSED' ? previousTask.closed_at || now : previousTask.closed_at,
      updated_at: now,
    };
    const optimisticTasks = currentTasks.map((task) => (task.id === taskId ? optimistic : task));
    const previousIncident = incidents.find((incident) => incident.id === incidentId);
    const nextIncidentStatus = previousIncident
      ? deriveIncidentStatusFromTasks(previousIncident.status, optimisticTasks)
      : undefined;
    setLocalTasks((current) => ({ ...current, [incidentId]: optimisticTasks }));
    setIncidents((current) =>
      current.map((incident) =>
        incident.id === incidentId
          ? {
              ...incident,
              status: deriveIncidentStatusFromTasks(incident.status, optimisticTasks),
              updated_at: now,
            }
          : incident
      )
    );
    if (previousIncident && nextIncidentStatus && previousIncident.status !== nextIncidentStatus) {
      appendLocalStatusHistory(
        incidentId,
        previousIncident.status,
        nextIncidentStatus,
        t('incident.detail.workflow.taskSyncReason')
      );
    }

    if (!isUuid(incidentId)) {
      messageApi.warning(t('app.messages.localTaskUpdated'));
      return optimistic;
    }

    try {
      const updated = await updateIncidentTask(incidentId, taskId, payload);
      const refreshedTasks = await listIncidentTasks(incidentId).catch(() => null);
      const latestTasks = localTasksRef.current[incidentId] || optimisticTasks;
      const updatedTasks = refreshedTasks || latestTasks.map((task) => (task.id === taskId ? updated : task));
      setLocalTasks((current) => ({ ...current, [incidentId]: updatedTasks }));
      setIncidents((current) =>
        current.map((incident) =>
          incident.id === incidentId
            ? {
                ...incident,
                status: deriveIncidentStatusFromTasks(incident.status, updatedTasks),
                updated_at: new Date().toISOString(),
              }
            : incident
        )
      );
      messageApi.success(t('app.messages.taskUpdated'));
      return updated;
    } catch {
      messageApi.warning(t('app.messages.remoteTaskUpdatedLocally'));
      return optimistic;
    }
  }, [appendLocalStatusHistory, incidents, messageApi, t]);

  const handleLoadTaskActivities = useCallback(async (incidentId: string): Promise<TaskActivity[]> => {
    if (!isUuid(incidentId)) {
      return localTaskActivitiesRef.current[incidentId] || [];
    }

    try {
      const activities = await listTaskActivities(incidentId);
      setLocalTaskActivities((current) => ({ ...current, [incidentId]: activities }));
      return activities;
    } catch {
      return localTaskActivitiesRef.current[incidentId] || [];
    }
  }, []);

  const handleLoadProcessTimeline = useCallback(async (incidentId: string): Promise<ProcessTimeline | null> => {
    if (!isUuid(incidentId)) {
      return null;
    }
    try {
      return await getProcessTimeline(incidentId);
    } catch {
      return null;
    }
  }, []);

  const handleDeleteTaskActivity = useCallback(async (incidentId: string, activityId: string): Promise<void> => {
    if (!isUuid(incidentId)) {
      setLocalTaskActivities((current) => ({
        ...current,
        [incidentId]: (current[incidentId] || []).filter((activity) => activity.id !== activityId),
      }));
      return;
    }

    await deleteTaskActivity(incidentId, activityId);
    const [tasks, activities] = await Promise.all([
      listIncidentTasks(incidentId),
      listTaskActivities(incidentId),
    ]);
    setLocalTasks((current) => ({ ...current, [incidentId]: tasks }));
    setLocalTaskActivities((current) => ({ ...current, [incidentId]: activities }));
  }, []);

  const handleUpdateTaskActivity = useCallback(async (
    incidentId: string,
    activityId: string,
    payload: TaskActivityUpdate
  ): Promise<TaskActivity> => {
    const updated = await updateTaskActivity(incidentId, activityId, payload);
    const [tasks, activities] = await Promise.all([
      listIncidentTasks(incidentId),
      listTaskActivities(incidentId),
    ]);
    setLocalTasks((current) => ({ ...current, [incidentId]: tasks }));
    setLocalTaskActivities((current) => ({ ...current, [incidentId]: activities }));
    return updated;
  }, []);

  const handleLoadTaskActivityEdits = useCallback(async (incidentId: string, activityId: string): Promise<AuditEdit[]> => {
    if (!isUuid(incidentId)) return [];
    return listTaskActivityEdits(incidentId, activityId);
  }, []);

  const handleLoadFieldChanges = useCallback(async (incidentId: string): Promise<IncidentFieldChange[]> => {
    if (!isUuid(incidentId)) {
      return localFieldChangesRef.current[incidentId] || [];
    }

    try {
      const changes = await listIncidentFieldChanges(incidentId);
      setLocalFieldChanges((current) => ({ ...current, [incidentId]: changes }));
      return changes;
    } catch {
      return localFieldChangesRef.current[incidentId] || [];
    }
  }, []);

  const handleCloseIncident = useCallback(async (incidentId: string, comment: string): Promise<Incident> => {
    const previous = incidents.find((incident) => incident.id === incidentId);
    if (!previous) {
      throw new Error('Incident not found');
    }

    const now = new Date().toISOString();
    const optimistic: Incident = {
      ...previous,
      status: 'CLOSED',
      updated_at: now,
    };
    setIncidents((current) => current.map((incident) => (incident.id === incidentId ? optimistic : incident)));
    appendLocalStatusHistory(incidentId, previous.status, 'CLOSED', comment);
    setLocalComments((current) => ({
      ...current,
      [incidentId]: [
        ...(current[incidentId] || []),
        {
          id: crypto.randomUUID(),
          incident_id: incidentId,
          author_user_id: currentUser?.id || null,
          author_display_name: currentUser?.display_name || null,
          author_profile_image_url: currentUser?.profile_image_url || null,
          body: comment,
          created_at: now,
          updated_at: now,
        },
      ],
    }));

    if (!isUuid(incidentId)) {
      messageApi.warning(t('app.messages.localIncidentClosed'));
      return optimistic;
    }

    try {
      const updated = await closeIncident(incidentId, { comment });
      setIncidents((current) => current.map((incident) => (incident.id === incidentId ? updated : incident)));
      const history = await listIncidentStatusHistory(incidentId);
      setLocalStatusHistory((current) => ({ ...current, [incidentId]: history }));
      messageApi.success(t('app.messages.incidentClosed'));
      return updated;
    } catch {
      messageApi.warning(t('app.messages.remoteIncidentClosedLocally'));
      return optimistic;
    }
  }, [appendLocalStatusHistory, currentUser?.display_name, currentUser?.id, currentUser?.profile_image_url, incidents, messageApi, t]);

  const handleAddComment = useCallback(async (incidentId: string, body: string): Promise<IncidentComment> => {
    if (!isUuid(incidentId)) {
      const comment: IncidentComment = {
        id: crypto.randomUUID(),
        incident_id: incidentId,
        author_user_id: currentUser?.id || null,
        author_display_name: currentUser?.display_name || null,
        author_profile_image_url: currentUser?.profile_image_url || null,
        body,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setLocalComments((current) => ({
        ...current,
        [incidentId]: [...(current[incidentId] || []), comment],
      }));
      messageApi.warning(t('app.messages.localCommentAdded'));
      return comment;
    }

    try {
      const comment = await addIncidentComment(incidentId, body);
      setLocalComments((current) => ({
        ...current,
        [incidentId]: [...(current[incidentId] || []), comment],
      }));
      messageApi.success(t('app.messages.commentAdded'));
      return comment;
    } catch {
      const comment: IncidentComment = {
        id: crypto.randomUUID(),
        incident_id: incidentId,
        author_user_id: currentUser?.id || null,
        author_display_name: currentUser?.display_name || null,
        author_profile_image_url: currentUser?.profile_image_url || null,
        body,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setLocalComments((current) => ({
        ...current,
        [incidentId]: [...(current[incidentId] || []), comment],
      }));
      messageApi.warning(t('app.messages.remoteCommentAddedLocally'));
      return comment;
    }
  }, [currentUser?.display_name, currentUser?.id, currentUser?.profile_image_url, messageApi, t]);

  const handleUpdateComment = useCallback(async (
    incidentId: string,
    commentId: string,
    body: string
  ): Promise<IncidentComment> => {
    const currentComment = (localCommentsRef.current[incidentId] || []).find((comment) => comment.id === commentId);
    if (!currentComment) {
      throw new Error('Comment not found');
    }
    const optimistic: IncidentComment = {
      ...currentComment,
      body,
      edited_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setLocalComments((current) => ({
      ...current,
      [incidentId]: (current[incidentId] || []).map((comment) => (comment.id === commentId ? optimistic : comment)),
    }));

    if (!isUuid(incidentId)) {
      messageApi.warning(t('app.messages.localCommentUpdated'));
      return optimistic;
    }

    try {
      const updated = await updateIncidentComment(incidentId, commentId, body);
      setLocalComments((current) => ({
        ...current,
        [incidentId]: (current[incidentId] || []).map((comment) => (comment.id === commentId ? updated : comment)),
      }));
      messageApi.success(t('app.messages.commentUpdated'));
      return updated;
    } catch {
      messageApi.warning(t('app.messages.remoteCommentUpdatedLocally'));
      return optimistic;
    }
  }, [messageApi, t]);

  const handleDeleteComment = useCallback(async (incidentId: string, commentId: string): Promise<void> => {
    setLocalComments((current) => ({
      ...current,
      [incidentId]: (current[incidentId] || []).filter((comment) => comment.id !== commentId),
    }));

    if (!isUuid(incidentId)) {
      messageApi.warning(t('app.messages.localCommentDeleted'));
      return;
    }

    try {
      await deleteIncidentComment(incidentId, commentId);
      messageApi.success(t('app.messages.commentDeleted'));
    } catch {
      const comments = await listIncidentComments(incidentId).catch(() => localCommentsRef.current[incidentId] || []);
      setLocalComments((current) => ({ ...current, [incidentId]: comments }));
      messageApi.warning(t('app.messages.remoteCommentDeleteFailed'));
    }
  }, [messageApi, t]);

  const handleAddTaskInternalComment = useCallback(async (
    incidentId: string,
    taskId: string,
    body: string
  ): Promise<IncidentTaskInternalComment> => {
    const cacheKey = taskInternalCommentKey(incidentId, taskId);
    if (!isUuid(incidentId) || !isUuid(taskId)) {
      const comment: IncidentTaskInternalComment = {
        id: crypto.randomUUID(),
        incident_id: incidentId,
        task_id: taskId,
        author_user_id: currentUser?.id || null,
        author_display_name: currentUser?.display_name || null,
        author_profile_image_url: currentUser?.profile_image_url || null,
        body,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setLocalTaskInternalComments((current) => ({
        ...current,
        [cacheKey]: [...(current[cacheKey] || []), comment],
      }));
      messageApi.warning(t('app.messages.localCommentAdded'));
      return comment;
    }

    try {
      const comment = await addIncidentTaskInternalComment(incidentId, taskId, body);
      setLocalTaskInternalComments((current) => ({
        ...current,
        [cacheKey]: [...(current[cacheKey] || []), comment],
      }));
      messageApi.success(t('app.messages.commentAdded'));
      return comment;
    } catch {
      const comment: IncidentTaskInternalComment = {
        id: crypto.randomUUID(),
        incident_id: incidentId,
        task_id: taskId,
        author_user_id: currentUser?.id || null,
        author_display_name: currentUser?.display_name || null,
        author_profile_image_url: currentUser?.profile_image_url || null,
        body,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setLocalTaskInternalComments((current) => ({
        ...current,
        [cacheKey]: [...(current[cacheKey] || []), comment],
      }));
      messageApi.warning(t('app.messages.remoteCommentAddedLocally'));
      return comment;
    }
  }, [currentUser?.display_name, currentUser?.id, currentUser?.profile_image_url, messageApi, t]);

  const handleUpdateTaskInternalComment = useCallback(async (
    incidentId: string,
    taskId: string,
    commentId: string,
    body: string
  ): Promise<IncidentTaskInternalComment> => {
    const cacheKey = taskInternalCommentKey(incidentId, taskId);
    const currentComment = (localTaskInternalCommentsRef.current[cacheKey] || []).find((comment) => comment.id === commentId);
    if (!currentComment) {
      throw new Error('Internal comment not found');
    }
    const optimistic: IncidentTaskInternalComment = {
      ...currentComment,
      body,
      edited_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setLocalTaskInternalComments((current) => ({
      ...current,
      [cacheKey]: (current[cacheKey] || []).map((comment) => (comment.id === commentId ? optimistic : comment)),
    }));

    if (!isUuid(incidentId) || !isUuid(taskId)) {
      messageApi.warning(t('app.messages.localCommentUpdated'));
      return optimistic;
    }

    try {
      const updated = await updateIncidentTaskInternalComment(incidentId, taskId, commentId, body);
      setLocalTaskInternalComments((current) => ({
        ...current,
        [cacheKey]: (current[cacheKey] || []).map((comment) => (comment.id === commentId ? updated : comment)),
      }));
      messageApi.success(t('app.messages.commentUpdated'));
      return updated;
    } catch {
      messageApi.warning(t('app.messages.remoteCommentUpdatedLocally'));
      return optimistic;
    }
  }, [messageApi, t]);

  const handleDeleteTaskInternalComment = useCallback(async (
    incidentId: string,
    taskId: string,
    commentId: string
  ): Promise<void> => {
    const cacheKey = taskInternalCommentKey(incidentId, taskId);
    setLocalTaskInternalComments((current) => ({
      ...current,
      [cacheKey]: (current[cacheKey] || []).filter((comment) => comment.id !== commentId),
    }));

    if (!isUuid(incidentId) || !isUuid(taskId)) {
      messageApi.warning(t('app.messages.localCommentDeleted'));
      return;
    }

    try {
      await deleteIncidentTaskInternalComment(incidentId, taskId, commentId);
      messageApi.success(t('app.messages.commentDeleted'));
    } catch {
      const comments = await listIncidentTaskInternalComments(incidentId, taskId).catch(
        () => localTaskInternalCommentsRef.current[cacheKey] || []
      );
      setLocalTaskInternalComments((current) => ({ ...current, [cacheKey]: comments }));
      messageApi.warning(t('app.messages.remoteCommentDeleteFailed'));
    }
  }, [messageApi, t]);

  const handleAttachFile = useCallback(async (
    incidentId: string,
    file: File,
    options?: { category?: AttachmentCategory; description?: string | null }
  ): Promise<IncidentAttachment> => {
    if (file.size > maxAttachmentSizeBytes) {
      messageApi.error(t('app.messages.attachmentTooLarge'));
      throw new Error('Attachment is larger than 50MB');
    }
    const category = options?.category || inferAttachmentCategory(file);
    const description = options?.description || null;
    if (!isUuid(incidentId)) {
      const attachment: IncidentAttachment = {
        id: crypto.randomUUID(),
        incident_id: incidentId,
        uploaded_by_user_id: null,
        uploaded_by_display_name: currentUser?.display_name || 'Prototype User',
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        bucket_name: 'local',
        object_key: `local/${incidentId}/${file.name}`,
        category,
        description,
        thumbnail_status: 'UNSUPPORTED',
        is_deleted: false,
        deleted_at: null,
        deleted_by_user_id: null,
        delete_reason: null,
        created_at: new Date().toISOString(),
      };
      setLocalAttachments((current) => ({
        ...current,
        [incidentId]: [...(current[incidentId] || []), attachment],
      }));
      messageApi.warning(t('app.messages.localAttachmentAdded'));
      return attachment;
    }

    try {
      const presign = await createAttachmentPresign(incidentId, {
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        category,
        description,
      });
      const uploadResponse = await fetch(presign.upload_url, {
        method: 'PUT',
        headers: presign.headers,
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Attachment upload failed: ${uploadResponse.status}`);
      }
      const attachment = await addIncidentAttachment(incidentId, {
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        bucket_name: presign.bucket_name,
        object_key: presign.object_key,
        category,
        description,
      });
      setLocalAttachments((current) => ({
        ...current,
        [incidentId]: [attachment, ...(current[incidentId] || [])],
      }));
      messageApi.success(t('app.messages.attachmentPrepared'));
      return attachment;
    } catch {
      const attachment: IncidentAttachment = {
        id: crypto.randomUUID(),
        incident_id: incidentId,
        uploaded_by_user_id: null,
        uploaded_by_display_name: currentUser?.display_name || 'Prototype User',
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        bucket_name: 'local',
        object_key: `local/${incidentId}/${file.name}`,
        category,
        description,
        thumbnail_status: 'UNSUPPORTED',
        is_deleted: false,
        deleted_at: null,
        deleted_by_user_id: null,
        delete_reason: null,
        created_at: new Date().toISOString(),
      };
      setLocalAttachments((current) => ({
        ...current,
        [incidentId]: [...(current[incidentId] || []), attachment],
      }));
      messageApi.warning(t('app.messages.remoteAttachmentAddedLocally'));
      return attachment;
    }
  }, [currentUser?.display_name, messageApi, t]);

  const handleDeleteAttachment = useCallback(async (incidentId: string, attachmentId: string): Promise<void> => {
    if (!isUuid(incidentId)) {
      setLocalAttachments((current) => ({
        ...current,
        [incidentId]: (current[incidentId] || []).filter((attachment) => attachment.id !== attachmentId),
      }));
      return;
    }

    await deleteIncidentAttachment(incidentId, attachmentId);
    setLocalAttachments((current) => ({
      ...current,
      [incidentId]: (current[incidentId] || []).filter((attachment) => attachment.id !== attachmentId),
    }));
  }, []);

  const handleCreateAttachmentDownload = useCallback(async (
    incidentId: string,
    attachmentId: string
  ): Promise<string> => {
    const attachment = localAttachmentsRef.current[incidentId]?.find((item) => item.id === attachmentId);
    if (!isUuid(incidentId)) {
      if (attachment?.upload_url) return attachment.upload_url;
      throw new Error('Local attachment preview is not available');
    }
    const response = await createAttachmentDownload(incidentId, attachmentId);
    return response.url;
  }, []);

  const handleCreateAttachmentThumbnailDownload = useCallback(async (
    incidentId: string,
    attachmentId: string
  ): Promise<string> => {
    const response = await createAttachmentThumbnailDownload(incidentId, attachmentId);
    return response.url;
  }, []);

  if (appInitPhase === 'bootstrapping') {
    return (
      <Layout className="appLayout">
        <div className="bootSplash">
          <div className="brandMark">GIB</div>
          <Spin />
          <Text className="bootSplashText">{t('auth.sessionRestoring')}</Text>
        </div>
      </Layout>
    );
  }

  if (appInitPhase === 'unauthenticated') {
    return (
      <Layout className="appLayout">
        {messageContextHolder}
        <div className="loginPane">
          <div className="loginPanel">
            <div className="brandMark">GIB</div>
            <h1>{t('auth.title')}</h1>
            <p>{t('auth.description')}</p>
            <Button type="primary" size="large" onClick={() => login()}>
              {t('auth.signIn')}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (appInitPhase === 'initializationError') {
    return (
      <Layout className="appLayout">
        {messageContextHolder}
        <div className="loginPane">
          <div className="loginPanel">
            <div className="brandMark">GIB</div>
            <h1>{initializationError?.title || t('auth.initializationErrorTitle')}</h1>
            <p>{initializationError?.message || t('auth.initializationError')}</p>
            <Button type="primary" size="large" onClick={() => window.location.reload()}>
              {t('auth.retry')}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (isPublicEmailVerificationLink && !authUser) {
    return (
      <Layout className="appLayout">
        {messageContextHolder}
        <Content className="content">
          <EmailVerificationPage currentUser={currentUser} onUserUpdated={setCurrentUser} />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="appLayout">
      {messageContextHolder}
      {sessionRefreshing && (
        <div className="sessionRefreshOverlay">
          <Spin size="small" />
          <Text>{t('auth.sessionRefreshing')}</Text>
        </div>
      )}
      <Sider width={232} breakpoint="lg" collapsedWidth="0">
        <div className="brand">
          <div className="brandMark">GIB</div>
          <div>
            <div className="brandName">Global Information Bank</div>
            <div className="brandSub">{t('app.brandSub')}</div>
          </div>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          onClick={(event) => navigate(event.key)}
          items={[
            { key: '/me', icon: <UserOutlined />, label: t('app.nav.myPage') },
            { key: '/factories', icon: <BuildOutlined />, label: t('app.nav.factories') },
            { key: '/service-locations', icon: <ShopOutlined />, label: t('app.nav.serviceLocations') },
            { key: '/incidents', icon: <FileTextOutlined />, label: t('app.nav.incidents') },
            {
              key: '/incidents/new',
              disabled: !canCreate,
              icon: <PlusCircleOutlined />,
              label: t('app.nav.newIncident'),
            },
            { key: '/help', icon: <QuestionCircleOutlined />, label: t('app.nav.helpCenter') },
          ]}
        />
        {canAccessUserManagement && (
          <>
            <div className="sidebarSectionTitle">{t('app.nav.adminMenu')}</div>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={selectedKey ? [selectedKey] : []}
              onClick={(event) => navigate(event.key)}
              items={[
                { key: '/admin/users', icon: <TeamOutlined />, label: t('app.nav.userManagement') },
                { key: '/admin/master/factories', icon: <BuildOutlined />, label: t('app.nav.factoryManagement') },
                { key: '/admin/master/service-locations', icon: <ShopOutlined />, label: t('app.nav.serviceLocationManagement') },
                { key: '/admin/master/products', icon: <AppstoreOutlined />, label: t('app.nav.productManagement') },
                { key: '/admin/forms', icon: <FormOutlined />, label: t('app.nav.formBuilder') },
                { key: '/admin/announcements', icon: <BellOutlined />, label: t('app.nav.announcementManagement') },
                { key: '/admin/help', icon: <QuestionCircleOutlined />, label: t('app.nav.helpManagement') },
              ]}
            />
          </>
        )}
      </Sider>
      <Layout>
        <Header className="topHeader">
          <Space className="headerTitleGroup" size={8}>
            <Text className="headerPageTitle">{pageTitle}</Text>
            {pageHelpHref ? (
              <Tooltip title={t('help.contextHelp')}>
                <Button
                  aria-label={t('help.contextHelp')}
                  className="headerHelpButton"
                  href={pageHelpHref}
                  icon={<QuestionCircleOutlined />}
                  shape="circle"
                  size="small"
                  type="text"
                />
              </Tooltip>
            ) : null}
          </Space>
          <Space className="headerControls" wrap>
            <NotificationBell
              announcements={announcements}
              unreadCount={announcementUnreadCount}
              loading={announcementsLoading}
              onOpenAnnouncements={() => navigate('/announcements')}
              onMarkRead={handleMarkAnnouncementRead}
              onMarkAllRead={handleMarkAllAnnouncementsRead}
            />
            <Tooltip title={currentUser?.email || ''}>
              <span>
                <UserMenu
                  currentUser={currentUser}
                  isAuthConfigured={isAuthConfigured}
                  languages={appLanguages}
                  onNavigateMyPage={() => navigate('/me')}
                  onLanguageChange={handleLanguageChange}
                />
              </span>
            </Tooltip>
          </Space>
        </Header>
        <Content className="content">
          {apiWarning && <Alert className="apiWarning" type="warning" showIcon message={apiWarning} />}
          <Routes>
              <Route
                path="/me"
                element={
                  <MyPage
                    currentUser={currentUser}
                    incidents={visibleIncidents}
                    attachmentsByIncident={localAttachments}
                    tasksByIncident={localTasks}
                    onUserUpdated={setCurrentUser}
                  />
                }
              />
              <Route
                path="/email-verification"
                element={<EmailVerificationPage currentUser={currentUser} onUserUpdated={setCurrentUser} />}
              />
              <Route
                path="/admin/users"
                element={
                  canAccessUserManagement ? (
                    <UserManagementPage currentUser={currentUser} />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('adminUsers.forbidden')} />
                    </div>
                  )
                }
              />
              <Route
                path="/admin/announcements"
                element={
                  canAccessUserManagement ? (
                    <AdminAnnouncementsPage currentUser={currentUser} />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('adminAnnouncements.forbidden')} />
                    </div>
                  )
                }
              />
              <Route
                path="/admin/help"
                element={
                  canAccessUserManagement ? (
                    <AdminHelpPage currentUser={currentUser} />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('adminHelp.forbidden')} />
                    </div>
                  )
                }
              />
              <Route
                path="/admin/master/factories"
                element={
                  canAccessUserManagement ? (
                    <AdminMasterPage currentUser={currentUser} kind="factories" />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('adminMaster.forbidden')} />
                    </div>
                  )
                }
              />
              <Route
                path="/admin/master/service-locations"
                element={
                  canAccessUserManagement ? (
                    <AdminMasterPage currentUser={currentUser} kind="serviceLocations" />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('adminMaster.forbidden')} />
                    </div>
                  )
                }
              />
              <Route
                path="/admin/master/products"
                element={
                  canAccessUserManagement ? (
                    <AdminProductMasterPage currentUser={currentUser} />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('adminProductMaster.forbidden')} />
                    </div>
                  )
                }
              />
              <Route
                path="/admin/forms"
                element={
                  canAccessUserManagement ? (
                    <AdminFormBuilderPage currentUser={currentUser} />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('adminFormBuilder.forbidden')} />
                    </div>
                  )
                }
              />
              <Route
                path="/announcements"
                element={
                  <AnnouncementsPage
                    announcements={announcements}
                    loading={announcementsLoading}
                    onMarkRead={handleMarkAnnouncementRead}
                    onMarkAllRead={handleMarkAllAnnouncementsRead}
                  />
                }
              />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/help/:slug" element={<HelpPage />} />
              <Route
                path="/"
                element={<Navigate to="/me" replace />}
              />
              <Route
                path="/factories"
                element={<FactoryListPage />}
              />
              <Route
                path="/factories/:factoryId"
                element={<FactoryDetailPage currentUser={currentUser} />}
              />
              <Route
                path="/service-locations"
                element={<ServiceLocationListPage />}
              />
              <Route
                path="/service-locations/:serviceLocationId"
                element={<ServiceLocationDetailPage currentUser={currentUser} />}
              />
              <Route
                path="/incidents"
                element={
                  <IncidentListPage
                    incidents={visibleIncidents}
                    attachmentsByIncident={localAttachments}
                    tasksByIncident={localTasks}
                    currentUser={currentUser}
                    assignmentFilter={assignmentFilter}
                    onAssignmentFilterChange={setAssignmentFilter}
                  />
                }
              />
              <Route
                path="/incidents/new"
                element={
                  canCreate ? (
                    <IncidentCreatePage onCreate={handleCreate} onDraft={handleDraft} onAttachFile={handleAttachFile} />
                  ) : (
                    <div className="page">
                      <Alert type="warning" showIcon message={t('app.messages.requiresIssuingParty')} />
                    </div>
                  )
                }
              />
              <Route
                path="/incidents/:incidentId"
                element={
                  <IncidentDetailPage
                    incidents={visibleIncidents}
                    attachmentsByIncident={localAttachments}
                    currentUser={currentUser}
                    onAddComment={handleAddComment}
                    onUpdateComment={handleUpdateComment}
                    onDeleteComment={handleDeleteComment}
                    onAddTaskInternalComment={handleAddTaskInternalComment}
                    onUpdateTaskInternalComment={handleUpdateTaskInternalComment}
                    onDeleteTaskInternalComment={handleDeleteTaskInternalComment}
                    onAttachFile={handleAttachFile}
                    onDeleteAttachment={handleDeleteAttachment}
                    onCreateAttachmentDownload={handleCreateAttachmentDownload}
                    onCreateAttachmentThumbnailDownload={handleCreateAttachmentThumbnailDownload}
                    onLoadComments={handleLoadComments}
                    onLoadTaskInternalComments={handleLoadTaskInternalComments}
                    onLoadAttachments={handleLoadAttachments}
                    onLoadTasks={handleLoadTasks}
                    onLoadRequests={handleLoadRequests}
                    onLoadTaskActivities={handleLoadTaskActivities}
                    onLoadProcessTimeline={handleLoadProcessTimeline}
                    onLoadFieldChanges={handleLoadFieldChanges}
                    onDeleteTaskActivity={handleDeleteTaskActivity}
                    onUpdateTaskActivity={handleUpdateTaskActivity}
                    onLoadTaskActivityEdits={handleLoadTaskActivityEdits}
                    onCreateRequest={handleCreateRequest}
                    onUpdateRequest={handleUpdateRequest}
                    onLoadRequestEdits={handleLoadRequestEdits}
                    onUpdateRequestActivity={handleUpdateRequestActivity}
                    onLoadRequestActivityEdits={handleLoadRequestActivityEdits}
                    onUpdateTask={handleUpdateTask}
                    onUpdateIncident={handleUpdateIncident}
                    onSetIncidentWatch={handleSetIncidentWatch}
                    onSetIncidentUseful={handleSetIncidentUseful}
                    onCloseIncident={handleCloseIncident}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

const App = () => {
  const { i18n, t } = useTranslation();
  const antdLocales = {
    de: deDE,
    en: enUS,
    es: esES,
    fr: frFR,
    id: idID,
    it: itIT,
    ja: jaJP,
    ms: msMY,
    nl: nlNL,
    'pt-BR': ptBR,
    th: thTH,
    'zh-CN': zhCN,
  };
  const locale = antdLocales[i18n.language as keyof typeof antdLocales] || enUS;

  return (
    <ConfigProvider
      locale={locale}
      form={{ validateMessages: { required: t('validation.required') } }}
      theme={{
        token: {
          colorPrimary: '#2563eb',
          borderRadius: 6,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans JP", sans-serif',
        },
      }}
    >
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
